import { createPollingInterval } from "@sg-protect/core";
import type { MethodResponse } from "openapi-fetch";
import { type client, mutationOptions, queryOptions } from "../../client.ts";
import type {
    BulkAction,
    BulkItemType,
    BulkOperationStatus,
    ODataBulkRequest,
    UpdateTenantFileVersionPolicyParameters
} from "../../types.ts";
import { isTerminalBulkStatus } from "../../utils/bulkStatusUtils.ts";
import { bulkOperationsQueryOptions } from "../odata/bulk-operations.ts";

type BulkSearchResponse = MethodResponse<typeof client, "get", "/bff/remediation/bulk/search">;
type BulkSummaryResponse = MethodResponse<typeof client, "get", "/bff/remediation/bulk/{bulkId}/summary">;

const bulkSearchRefetchInterval = createPollingInterval<BulkSearchResponse>({
    isPending: data => Boolean(data?.length),
    timeout: Infinity
});

const bulkSummaryRefetchInterval = createPollingInterval<BulkSummaryResponse>({
    isPending: data => !isTerminalBulkStatus(data?.status),
    timeout: Infinity
});

interface BulkSearchQueryParams {
    entityId: string;
    entityType: BulkItemType;
    action?: BulkAction;
    statuses: BulkOperationStatus[];
}

export function bulkSearchQueryOptions({ entityId, entityType, action, statuses }: BulkSearchQueryParams) {
    return queryOptions(
        "get",
        "/bff/remediation/bulk/search",
        { params: { query: { entityId, entityType, action, statuses } } },
        {
            enabled: !!entityId,
            refetchInterval: bulkSearchRefetchInterval
        }
    );
}

export function bulkSummaryQueryOptions(bulkId: string) {
    return queryOptions(
        "get",
        "/bff/remediation/bulk/{bulkId}/summary",
        { params: { path: { bulkId } } },
        {
            refetchInterval: bulkSummaryRefetchInterval
        }
    );
}

export function archivingEnabledQueryOptions() {
    return queryOptions(
        "get",
        "/bff/remediation/bulk/checkArchivingEnabled",
        {},
        {
            staleTime: ({ state: { data } }) => (data?.isEnabled ? Infinity : 0)
        }
    );
}

export function planODataBulkMutationOptions() {
    return mutationOptions("post", "/bff/remediation/bulk/odata/plan");
}

export function planSharePointEntityBulkMutationOptions() {
    return mutationOptions("post", "/bff/remediation/bulk/sharePointEntities/plan");
}

export function startBulkMutationOptions() {
    return mutationOptions("post", "/bff/remediation/bulk/{bulkId}/start", {
        onSuccess: async (_data, _variables, _onMutateResult, { client }) => {
            await Promise.all([
                // Search params are required by the schema, but discarded here via slice — this key only
                // needs to be the whole-endpoint prefix ["get", path] for a broad invalidation.
                client.invalidateQueries({
                    queryKey: queryOptions("get", "/bff/remediation/bulk/search", {
                        params: { query: { entityId: "", entityType: "user", statuses: [] } }
                    }).queryKey.slice(0, 2)
                }),
                client.invalidateQueries({ queryKey: bulkOperationsQueryOptions().queryKey })
            ]);
        }
    });
}

export function dismissBulkMutationOptions() {
    return mutationOptions("post", "/bff/remediation/bulk/{bulkId}/dismiss", {
        onSuccess: async (_data, _variables, _onMutateResult, { client }) => {
            await client.invalidateQueries({ queryKey: bulkOperationsQueryOptions().queryKey });
        }
    });
}

export function toUpdateTenantFileVersionPolicyBody(params: UpdateTenantFileVersionPolicyParameters): ODataBulkRequest {
    return {
        entitySet: "tenantSettings",
        query: "",
        action: "updateTenantFileVersionPolicy",
        parameters: params,
        includes: [],
        excludes: []
    };
}
